import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "config/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // Starts true: on a page load we do not yet know whether the cookie is valid,
  // and guards must not treat "unknown" as "signed out" or every refresh would
  // bounce the user to the sign-in page before the check comes back.
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // The token lives in an httpOnly cookie, which JavaScript cannot read, so
  // identity has to come from the API rather than from decoding a JWT locally.
  const refreshUser = useCallback(async () => {
    try {
      const response = await apiFetch("/api/auth/me");
      setUser(response.ok ? await response.json() : null);
    } catch {
      // Network failure is not proof of being signed out, but there is nothing
      // to render a session from either.
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = async (username, password) => {
    const body = new URLSearchParams({ username, password });
    const response = await apiFetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || "Invalid credentials");
    }

    // The response body carries a bearer token for API clients; the browser
    // ignores it and relies on the cookie the server just set.
    await refreshUser();
  };

  const logout = async () => {
    try {
      await apiFetch("/api/auth/logout", { method: "POST" });
    } catch {
      // Clear locally regardless: a failed call must not strand a signed-in UI.
    }
    setUser(null);
    navigate("/login", { replace: true });
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
